import { IRows, IRow, IColumn, IDocumentBlock } from '@models/Forms/IForms';

export const EXTERNAL_DATA_SOURCE_PREFIX = '|ExternalDataSource|';

export type EnsureExternalDataSourceLoaded = (sourceName: string) => Promise<void>;

export interface IExternalDataSourceKey {
    sourceName: string;
    fieldName: string;
}

export interface IExternalDataSourceArrayKey {
    sourceName: string;
}

const externalKeyRegex = /\|ExternalDataSource\|([^|{}\s]+)\|([^|{}\s]+)/g;

const externalArrayKeyRegex = /^\|ExternalDataSource\|([^|{}\s]+)$/;

export const parseExternalDataSourceArrayKey = (key?: string): IExternalDataSourceArrayKey | undefined => {
    if (!key) {
        return undefined;
    }

    const match = key.match(externalArrayKeyRegex);
    if (!match) {
        return undefined;
    }

    return { sourceName: match[1] };
};

export const parseExternalDataSourceKey = (key?: string): IExternalDataSourceKey | undefined => {
    if (!key?.startsWith(EXTERNAL_DATA_SOURCE_PREFIX)) {
        return undefined;
    }

    const parts = key.split('|');
    if (parts.length < 4 || !parts[2] || !parts[3]) {
        return undefined;
    }

    return {
        sourceName: parts[2],
        fieldName: parts.slice(3).join('|'),
    };
};

export const extractExternalDataSourceKeys = (value?: string): string[] => {
    if (!value) {
        return [];
    }

    const keys = new Set<string>();
    for (const match of value.matchAll(externalKeyRegex)) {
        keys.add(`|ExternalDataSource|${match[1]}|${match[2]}`);
    }
    return Array.from(keys);
};

export const extractExternalDataSourceNames = (...values: (string | undefined)[]): string[] => {
    const names = new Set<string>();
    values.forEach((value) => {
        const directKey = parseExternalDataSourceKey(value);
        if (directKey) {
            names.add(directKey.sourceName);
            return;
        }

        const arrayKey = parseExternalDataSourceArrayKey(value);
        if (arrayKey) {
            names.add(arrayKey.sourceName);
            return;
        }

        extractExternalDataSourceKeys(value).forEach((key) => {
            const parsed = parseExternalDataSourceKey(key);
            if (parsed) {
                names.add(parsed.sourceName);
            }
        });
    });
    return Array.from(names);
};

export const collectExternalDataSourceFieldKeys = (rows?: IRows): string[] => {
    const keys = new Set<string>();

    const addFormulaKeys = (...rules: (string | undefined)[]) => {
        rules.forEach((rule) => {
            extractExternalDataSourceKeys(rule).forEach((key) => keys.add(key));
        });
    };

    const addKeyed = (item?: {
        key?: string;
        visibilityRules?: string;
        required?: string;
        readonly?: string;
        min?: string;
        max?: string;
        maxLengthRules?: string;
        formula?: string;
        displayFormat?: string;
        previewDocByKey?: string;
        value?: string;
        href?: string;
        nameFormula?: string;
    }) => {
        if (!item) {
            return;
        }

        if (item.key && (parseExternalDataSourceKey(item.key) || parseExternalDataSourceArrayKey(item.key))) {
            keys.add(item.key);
        }

        addFormulaKeys(
            item.visibilityRules,
            item.required,
            item.readonly,
            item.min,
            item.max,
            item.maxLengthRules,
            item.formula,
            item.displayFormat,
            item.previewDocByKey,
            item.value,
            item.href,
            item.nameFormula,
        );
    };

    const visitBlock = (block?: IDocumentBlock) => {
        addKeyed(block);
        block?.rows?.row?.forEach(visitRow);
    };

    const visitColumn = (column?: IColumn) => {
        addKeyed(column);
        column?.fields?.forEach(addKeyed);
        column?.calcs?.forEach(addKeyed);
        column?.dicts?.forEach(addKeyed);
        column?.abooks?.forEach(addKeyed);
        column?.autoCompletes?.forEach(addKeyed);
        column?.texts?.forEach(addKeyed);
        column?.hints?.forEach(addKeyed);
        column?.urls?.forEach(addKeyed);
        column?.tables?.forEach(addKeyed);
        column?.blocks?.forEach(visitBlock);
        column?.rows?.forEach(visitRow);
        column?.editInModals?.forEach((m) => m?.rows?.row?.forEach(visitRow));
    };

    const visitRow = (row?: IRow) => {
        addKeyed(row);
        row?.columns?.forEach(visitColumn);
    };

    rows?.rows?.row?.forEach(visitRow);
    return Array.from(keys);
};
